import { useState } from 'react'
import { useLocation, Link, useNavigate } from 'react-router-dom'
import { IconMenu2, IconChevronRight, IconUser, IconLogout, IconSettings, IconChevronDown, IconShield, IconEye, IconUsers } from '@tabler/icons-react'
import { useAuth } from '../../contexts/AuthContext'
import { Badge } from '../ui/badge'

interface HeaderProps {
  onMenuClick: () => void
}

// Breadcrumb mapping (etiquetes com al Sidebar)
const breadcrumbMap: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/calendar-v2': 'Calendari',
  '/calendari/historic': 'Calendari històric',
  '/clients': 'Clients',
  '/reserves': 'Reserves',
  '/casos': 'Casos',
  '/viatgers': 'Viatgers',
  '/persons': 'Persons',
  '/mossos': 'Mossos TXT',
  '/forms': 'Forms Públics',
  '/config': 'Configuració',
  '/manual': 'Manual',
  '/chat': 'Miranda',
  '/whatsapp': 'WhatsApp',
  '/workflows': 'Tasques',
  '/precontacte': 'Precontacte Expedia',
  '/precontacte-booking': 'Precontacte Booking',
  '/tools/castink': 'Castink',
  '/admin/data-import': 'Data Import',
  '/admin/invoices/upload': 'Upload Factures',
  '/admin/invoices/amazon-import': 'Amazon Import',
  '/admin/workflow-runs': 'Workflow Runs',
  '/admin/workflows': 'Workflows',
  '/admin/tokens': 'Tokens',
  '/admin/billing': 'Factures',
  '/admin/guest-portal': 'GuestPortal',
  '/admin/contracts': 'Contractes',
  '/admin/settings': 'Configuració',
  '/admin/locks': 'Panys TTLock',
  '/admin/rooms': 'Habitacions',
  '/admin/agents': 'Agents IA',
  '/admin/notifications': 'Notificacions',
  '/admin/climate': 'Clima/HVAC',
  '/admin/engineering': 'Engineering Log',
  '/admin/crawlers': 'Crawlers',
  '/admin/prices': 'Preus Competència',
  '/admin/bank-movements': 'Moviments Bancaris',
  '/admin/cobraments': 'Cobraments Stripe',
  '/admin/messaging-templates': 'Plantilles Messaging',
  '/admin/whatsapp': 'Informe WhatsApp',
}

interface Crumb {
  label: string
  to?: string
}

/**
 * Molles després d'«Inici». Rutes amb paràmetre: la pàgina pare enllaçada i el detall
 * (`/reserves/<localitzador>` → «Reserves › <localitzador>», `/whatsapp/<id>` → «WhatsApp › Conversa»).
 */
function breadcrumbTrail(pathname: string): Crumb[] {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname
  const exact = breadcrumbMap[path]
  if (exact) return [{ label: exact }]

  const cut = path.lastIndexOf('/')
  const parent = path.slice(0, cut)
  const parentLabel = breadcrumbMap[parent]
  if (cut > 0 && parentLabel) {
    let param = ''
    try {
      param = decodeURIComponent(path.slice(cut + 1))
    } catch {
      param = ''
    }
    const detail =
      parent === '/reserves'
        ? param || 'Reserva'
        : parent === '/whatsapp'
          ? 'Conversa'
          : parent === '/clients'
            ? 'Client'
            : parent === '/chat'
              ? 'Conversa'
              : 'Detall'
    return [{ label: parentLabel, to: parent }, { label: detail }]
  }
  return [{ label: 'Pàgina' }]
}

export default function Header({ onMenuClick }: HeaderProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const [showUserMenu, setShowUserMenu] = useState(false)
  
  const currentPath = location.pathname
  const trail = breadcrumbTrail(currentPath)

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  // Función para obtener el icono y color según el rol
  const getRoleBadge = () => {
    if (!user?.role) return null

    const roleConfig = {
      ADMIN: { icon: IconShield, label: 'Admin', className: 'bg-red-100 text-red-700 border-red-200' },
      STAFF: { icon: IconUsers, label: 'Staff', className: 'bg-blue-100 text-blue-700 border-blue-200' },
      VIEWER: { icon: IconEye, label: 'Viewer', className: 'bg-gray-100 text-gray-700 border-gray-200' },
    }

    const config = roleConfig[user.role]
    const Icon = config.icon

    return (
      <Badge variant="outline" className={`${config.className} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    )
  }

  return (
    <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 md:px-6">
      {/* Left section: Hamburger (mobile) + Breadcrumbs */}
      <div className="flex items-center space-x-4">
        {/* Hamburger Menu (Mobile Only) */}
        <button
          onClick={onMenuClick}
          className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
          aria-label="Obrir menú"
        >
          <IconMenu2 className="w-6 h-6 text-gray-700" />
        </button>

        {/* Breadcrumbs */}
        <div className="flex items-center space-x-2">
          <Link
            to="/dashboard"
            className="text-sm text-gray-500 hover:text-gray-700 transition-colors hidden sm:block"
          >
            Inici
          </Link>
          {currentPath !== '/dashboard' &&
            trail.map((crumb, index) => (
              <span key={`${index}-${crumb.label}`} className="flex min-w-0 items-center space-x-2">
                {/* A mòbil «Inici» i el pare no es veuen: només el xebró entre visibles. */}
                <IconChevronRight className="w-4 h-4 shrink-0 text-gray-400 hidden sm:block" />
                {crumb.to ? (
                  <Link
                    to={crumb.to}
                    className="text-sm text-gray-500 hover:text-gray-700 transition-colors hidden sm:block"
                  >
                    {crumb.label}
                  </Link>
                ) : (
                  <span className="truncate text-sm font-medium text-gray-900">{crumb.label}</span>
                )}
              </span>
            ))}
          {currentPath === '/dashboard' && (
            <span className="text-sm font-medium text-gray-900">Dashboard</span>
          )}
        </div>
      </div>

      {/* Right section: User profile with dropdown */}
      <div className="flex items-center space-x-4 relative">
        {/* User Avatar/Menu */}
        <div className="relative">
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
              <IconUser className="w-5 h-5 text-green-700" />
            </div>
            <div className="hidden sm:flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700">
                {user?.name || user?.email || 'Admin'}
              </span>
              {getRoleBadge()}
            </div>
            <IconChevronDown className="w-4 h-4 text-gray-500" />
          </button>
          
          {/* Dropdown Menu */}
          {showUserMenu && (
            <div className="absolute right-0 mt-2 w-56 rounded-lg shadow-lg bg-white border border-gray-200 py-1 z-50">
              <div className="px-4 py-2 border-b border-gray-100">
                <p className="text-sm font-medium text-gray-900">{user?.name || 'Usuari'}</p>
                <p className="text-xs text-gray-500">{user?.email}</p>
                <p className="text-xs text-gray-400 mt-1">Rol: {user?.role}</p>
              </div>
              
              <Link
                to="/config"
                onClick={() => setShowUserMenu(false)}
                className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <IconSettings className="w-4 h-4 mr-3" />
                Configuració
              </Link>
              
              <button
                onClick={handleLogout}
                className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
              >
                <IconLogout className="w-4 h-4 mr-3" />
                Tancar sessió
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  )
}
